import { useCallback, useState, type ReactNode } from 'react';
import { Alert, Box, Button, CircularProgress, Divider, IconButton, Stack, Typography } from '@mui/material';
import AssignmentIndOutlined from '@mui/icons-material/AssignmentIndOutlined';
import EventOutlined from '@mui/icons-material/EventOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import AddShoppingCartOutlined from '@mui/icons-material/AddShoppingCartOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ForwardToInbox from '@mui/icons-material/ForwardToInbox';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import { useEventStore } from '@/state/stores/use-event-store';
import { useTranslations } from '@/i18n/use-translations';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions';
import { EVENT_PERMISSIONS } from '@/enums/permissions.enum';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import useSendEventContributorsInvitations from '@/state/mutations/invitations/use-send-event-contributors-invitations';
import { useNotifications } from '@/hooks/use-notifications';
import {
  useInsufficientContributorCreditsInfo,
  useAddContributorCreditsFromDeficit,
} from '@/hooks/use-insufficient-contributor-credits-info';
import EventCatalogOrderCheckoutModal from '@/components/events/event-catalog/event-catalog-order-checkout.modal.tsx';
import usePatchContributorMutation from '@/state/mutations/contributors/use-patch-contributor';
import { usePatchContributorRoleMutation } from '@/state/mutations/contributors/use-patch-contributor-role';
import { usePatchContributorRoleSlotMutation } from '@/state/mutations/contributors/use-patch-contributor-role-slot';
import {
  applyStagedForInvitePlan,
  computeStagedForInvitePatches,
} from '@/components/events/detail/tabs/contributors/staging-selection-cascade.helpers';
import type { ContributorListDisplayVariant } from '@/components/flows/detail/flow-detail.types';
import { isSessionOrganizePublishedForInvite } from '@/utils/contributor-invite-publication.utils';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';

function findRoleSlotForWidget(contributor: ContributorDto, roleId: string, roleSlotIdHint: string) {
  const r = contributor.roles.find((x) => x.id === roleId);
  return r?.slots?.find(
    (sl) => sl.deleted !== true && (sl.id === roleSlotIdHint || String(sl.id) === String(roleSlotIdHint)),
  );
}

type ArmMode = 'none' | 'contribution' | 'timeSlot';

export type ContributorInviteTimeSlotState = {
  roleSlotId: string;
  invited: boolean;
  accepted: boolean;
};

export type ContributorInviteStatusPanelProps = {
  contributor: ContributorDto;
  roleId: string;
  roleInvited: boolean;
  roleAccepted: boolean;
  timeSlot?: ContributorInviteTimeSlotState | null;
  /** When true, no invite/send actions (e.g. ORGANIZE read-only). */
  disabled?: boolean;
  /** Block contribution-row invites until PLAN→ORGANIZE publication (flow or session). */
  publicationBlockContribution?: 'flow' | 'session' | null;
  /**
   * Block time-slot-row invites until parent flow/session or event schedule slot is published to ORGANIZE.
   * Includes `flow` / `session` when the whole flow/session must be published first (same copy as contribution row).
   */
  publicationBlockTimeSlot?: 'flow' | 'session' | 'flowSlot' | 'schedule' | null;
  /** Flow slot-assets column: hide per-row Invite; staging toggle uses Invite / On invite list. */
  displayVariant?: ContributorListDisplayVariant;
  /** When set (session actor widgets), ORGANIZE copy is checked from the live session ref. */
  sessionRef?: SessionDto;
};

export function ContributorInviteStatusPanel({
  contributor,
  roleId,
  roleInvited,
  roleAccepted,
  timeSlot,
  disabled: disabledProp,
  publicationBlockContribution = null,
  publicationBlockTimeSlot = null,
  displayVariant = 'default',
  sessionRef,
}: ContributorInviteStatusPanelProps) {
  const t = useTranslations();
  const notification = useNotifications();
  const event = useEventStore((s) => s.event);
  const contributors = useEventStore((s) => s.contributors);
  const eventSessionsPlan = useEventStore((s) => s.eventSessionsPlan);
  const eventSessionsOrganize = useEventStore((s) => s.eventSessionsOrganize);
  const mode = useEventMode();
  const { data: permissions } = usePermissionsQuery();
  const { mutateAsync: sendInvites, isPending } = useSendEventContributorsInvitations();
  const { mutateAsync: patchContributor } = usePatchContributorMutation();
  const { mutateAsync: patchContributorRole } = usePatchContributorRoleMutation();
  const { mutateAsync: patchContributorRoleSlot } = usePatchContributorRoleSlotMutation();

  const [armMode, setArmMode] = useState<ArmMode>('none');
  const [stagingBusy, setStagingBusy] = useState(false);

  const eventId = event?.id;
  const canInvite = !!eventId && !!permissions?.events?.[eventId]?.includes(EVENT_PERMISSIONS.INVITE_CONTRIBUTORS);

  const insufficientCredits = useInsufficientContributorCreditsInfo(event, contributors ?? undefined);
  const { handleAddMissingCredits, checkoutOpen, setCheckoutOpen } =
    useAddContributorCreditsFromDeficit(insufficientCredits);

  /**
   * Primitive selectors so nested role/slot `stagedForInvite` updates re-render (avoids stale slot OUTBOX UI).
   */
  const roleStagedForInvite = useEventStore(
    useCallback(
      (s) =>
        s.contributors?.find((c) => c.id === contributor.id)?.roles.find((r) => r.id === roleId)?.stagedForInvite ===
        true,
      [contributor.id, roleId],
    ),
  );
  const slotStagedForInvite = useEventStore(
    useCallback(
      (s) => {
        if (!timeSlot?.roleSlotId) return false;
        const c = s.contributors?.find((x) => x.id === contributor.id);
        if (!c) return false;
        const slot = findRoleSlotForWidget(c, roleId, timeSlot.roleSlotId);
        return slot?.stagedForInvite === true;
      },
      [contributor.id, roleId, timeSlot?.roleSlotId],
    ),
  );
  const timeSlotRowHasSlotEntity = useEventStore(
    useCallback(
      (s) => {
        if (!timeSlot?.roleSlotId) return false;
        const c = s.contributors?.find((x) => x.id === contributor.id);
        if (!c) return false;
        return !!findRoleSlotForWidget(c, roleId, timeSlot.roleSlotId);
      },
      [contributor.id, roleId, timeSlot?.roleSlotId],
    ),
  );

  /** Contribution row mirrors this role’s checkbox only. */
  const contributionOutboxVisualOn = roleStagedForInvite;
  const timeSlotOutboxVisualOn = slotStagedForInvite;

  /** INVITE/Send on a row only blocked when that row’s OUTBOX (staging) is on for that role or slot. */
  const inviteBaseDisabled = disabledProp || isOrganizeMode(mode) || !canInvite || isPending || stagingBusy;
  const inviteDisabledContributionRow = inviteBaseDisabled || roleStagedForInvite;
  const inviteDisabledTimeSlotRow = inviteBaseDisabled || slotStagedForInvite;

  /** OUTBOX: same as list checkboxes — always toggles staging unless blocked by permissions or a patch in flight. */
  const outboxDisabled = disabledProp || isOrganizeMode(mode) || !canInvite || stagingBusy;

  const toggleContributionStaging = useCallback(async () => {
    const c = useEventStore.getState().contributors?.find((x) => x.id === contributor.id);
    const r = c?.roles.find((x) => x.id === roleId);
    if (!c || !r) return;
    const plan = computeStagedForInvitePatches(c, {
      isSelected: !(r.stagedForInvite === true),
      contributorId: c.id,
      roleId,
    });
    try {
      setStagingBusy(true);
      await applyStagedForInvitePlan(plan, {
        patchContributor,
        patchContributorRole,
        patchContributorRoleSlot,
      });
    } catch {
      notification.notifyError(t('event.detail.contributors.notifications.updateContributorFailed'));
    } finally {
      setStagingBusy(false);
    }
  }, [contributor.id, roleId, patchContributor, patchContributorRole, patchContributorRoleSlot, notification, t]);

  const toggleTimeSlotStaging = useCallback(async () => {
    if (!timeSlot?.roleSlotId) return;
    const c = useEventStore.getState().contributors?.find((x) => x.id === contributor.id);
    if (!c) return;
    const slot = findRoleSlotForWidget(c, roleId, timeSlot.roleSlotId);
    if (!slot) return;
    const plan = computeStagedForInvitePatches(c, {
      isSelected: !(slot.stagedForInvite === true),
      contributorId: c.id,
      roleId,
      slotId: slot.id,
    });
    try {
      setStagingBusy(true);
      await applyStagedForInvitePlan(plan, {
        patchContributor,
        patchContributorRole,
        patchContributorRoleSlot,
      });
    } catch {
      notification.notifyError(t('event.detail.contributors.notifications.updateContributorFailed'));
    } finally {
      setStagingBusy(false);
    }
  }, [
    contributor.id,
    roleId,
    timeSlot?.roleSlotId,
    patchContributor,
    patchContributorRole,
    patchContributorRoleSlot,
    notification,
    t,
  ]);

  /** Match contributors-list: not-yet-event-invited contributors need a role row with empty slotIds (event invite), not only slot-specific IDs. */
  const rolesPayloadForInvite = useCallback(
    (slotIds: string[]): { roleId: string; slotIds: string[] }[] => {
      if (!contributor.invitedAt && slotIds.length > 0) {
        return [
          { roleId, slotIds: [] },
          { roleId, slotIds },
        ];
      }
      return [{ roleId, slotIds }];
    },
    [contributor.invitedAt, roleId],
  );

  const send = useCallback(
    async (slotIds: string[]) => {
      if (!eventId) return;
      try {
        await sendInvites({
          eventId,
          contributors: [
            {
              contributorId: contributor.id,
              roles: rolesPayloadForInvite(slotIds),
            },
          ],
        });
        notification.notifySuccess(t('event.detail.contributors.notifications.sendInvitationsSuccess'));
        setArmMode('none');
      } catch {
        notification.notifyError(t('event.detail.contributors.notifications.sendInvitationsFailed'));
      }
    },
    [eventId, sendInvites, contributor.id, rolesPayloadForInvite, notification, t],
  );

  const sessionPublishRequired = sessionRef
    ? !isSessionOrganizePublishedForInvite(sessionRef, eventSessionsPlan, eventSessionsOrganize)
    : publicationBlockContribution === 'session' || publicationBlockTimeSlot === 'session';

  if (sessionPublishRequired) {
    return (
      <Alert severity="info" sx={{ mb: 1, borderRadius: 1 }}>
        {t('contributor.inviteStatusPanel.publishSessionForContributorsInfo')}
      </Alert>
    );
  }

  const creditsBanner =
    insufficientCredits && !contributor.invitedAt ? (
      <>
        <Alert
          severity="info"
          sx={{ mb: 1, borderRadius: 1 }}
          action={
            <IconButton color="secondary" size="small" onClick={handleAddMissingCredits} sx={{ p: 0.5 }}>
              <AddShoppingCartOutlined />
            </IconButton>
          }
        >
          {t('event.detail.contributors.insufficientCredits', {
            needed: insufficientCredits.neededCredits,
            notInvited: insufficientCredits.notInvitedCount,
          })}
        </Alert>
        <EventCatalogOrderCheckoutModal open={checkoutOpen} event={event} onModalClose={() => setCheckoutOpen(false)} />
      </>
    ) : null;

  const renderStatusOrActions = (
    kind: 'contribution' | 'timeSlot',
    invited: boolean,
    accepted: boolean,
    roleSlotId?: string,
  ) => {
    if (accepted) {
      return (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'success.main' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
          <Typography variant="caption" fontWeight={600}>
            {t('contributor.inviteStatusPanel.confirmed')}
          </Typography>
        </Stack>
      );
    }
    if (invited) {
      return (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'info.main' }}>
          <HourglassEmptyIcon sx={{ fontSize: 18 }} />
          <Typography variant="caption" fontWeight={600}>
            {t('contributor.inviteStatusPanel.pending')}
          </Typography>
        </Stack>
      );
    }
    if (!contributor.email) {
      return (
        <Typography variant="caption" color="text.disabled">
          {t('contributor.inviteStatusPanel.addEmailToInvite')}
        </Typography>
      );
    }

    if (kind === 'contribution' && publicationBlockContribution === 'flow') {
      return (
        <Typography variant="caption" color="text.disabled">
          {t('contributor.inviteStatusPanel.publishFlowToInvite')}
        </Typography>
      );
    }
    if (kind === 'contribution' && publicationBlockContribution === 'session') {
      return (
        <Typography variant="caption" color="text.disabled">
          {t('contributor.inviteStatusPanel.publishSessionToInvite')}
        </Typography>
      );
    }
    if (kind === 'timeSlot' && publicationBlockTimeSlot === 'flow') {
      return (
        <Typography variant="caption" color="text.disabled">
          {t('contributor.inviteStatusPanel.publishFlowToInvite')}
        </Typography>
      );
    }
    if (kind === 'timeSlot' && publicationBlockTimeSlot === 'session') {
      return (
        <Typography variant="caption" color="text.disabled">
          {t('contributor.inviteStatusPanel.publishSessionToInvite')}
        </Typography>
      );
    }
    if (kind === 'timeSlot' && publicationBlockTimeSlot === 'flowSlot') {
      return (
        <Typography variant="caption" color="text.disabled">
          {t('contributor.inviteStatusPanel.publishFlowSlotToInvite')}
        </Typography>
      );
    }
    if (kind === 'timeSlot' && publicationBlockTimeSlot === 'schedule') {
      return (
        <Typography variant="caption" color="text.disabled">
          {t('contributor.inviteStatusPanel.publishFlowScheduleToInvite')}
        </Typography>
      );
    }

    const inviteThisRowDisabled = kind === 'contribution' ? inviteDisabledContributionRow : inviteDisabledTimeSlotRow;

    const sendButtonSx = { minWidth: 72, py: 0.25, fontSize: '0.7rem' } as const;
    const cancelButtonSx = { minWidth: 72, py: 0.25, fontSize: '0.7rem' } as const;
    const inviteListButtonStartIcon = <AddOutlinedIcon sx={{ fontSize: '1rem' }} />;
    const inviteListButtonSx = { minWidth: 108, py: 0.25, fontSize: '0.7rem', whiteSpace: 'nowrap' } as const;
    const sendStartIcon = isPending ? (
      <CircularProgress size={14} color="inherit" />
    ) : (
      <ForwardToInbox sx={{ fontSize: '1rem' }} />
    );

    const isFlowSlotAssetsUi = displayVariant === 'flowSlotAssets';

    const outboxToggle = (rowKind: 'contribution' | 'timeSlot') => {
      const visualOn = rowKind === 'contribution' ? contributionOutboxVisualOn : timeSlotOutboxVisualOn;
      const noSlotForTimeRow = rowKind === 'timeSlot' && !timeSlotRowHasSlotEntity;
      const outboxInviteListIcon =
        !visualOn && isFlowSlotAssetsUi ? <AddOutlinedIcon sx={{ fontSize: '1rem' }} /> : undefined;
      const outboxLabel = visualOn
        ? isFlowSlotAssetsUi
          ? t('contributor.inviteOutbox.onInviteListFlowSlotAssets')
          : t('contributor.inviteOutbox.onInviteList')
        : isFlowSlotAssetsUi
          ? t('contributor.inviteStatusPanel.inviteList')
          : t('contributor.inviteOutbox.addToInviteList');
      return (
        <Button
          size="small"
          variant={visualOn ? 'outlined' : 'contained'}
          color="primary"
          disabled={outboxDisabled || noSlotForTimeRow}
          onClick={rowKind === 'contribution' ? toggleContributionStaging : toggleTimeSlotStaging}
          startIcon={outboxInviteListIcon}
          sx={{
            py: 0.25,
            px: 1,
            fontSize: '0.7rem',
            whiteSpace: 'nowrap',
            ...(!visualOn && isFlowSlotAssetsUi ? { minWidth: 108 } : {}),
          }}
        >
          {outboxLabel}
        </Button>
      );
    };

    /** OUTBOX hides while that row shows Cancel/Send; Contribution OUTBOX also hides when the Time-Slot row is armed. */
    const withOutbox = (rowKind: 'contribution' | 'timeSlot', node: ReactNode) => {
      const hideOutboxOnThisRow =
        rowKind === 'contribution' ? armMode === 'contribution' || armMode === 'timeSlot' : armMode === 'timeSlot';
      if (hideOutboxOnThisRow) return node;
      return (
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" flexWrap="wrap">
          {node ? null : outboxToggle(rowKind)}
          {node}
        </Stack>
      );
    };

    if (kind === 'contribution') {
      if (armMode === 'timeSlot') {
        // No Cancel here: time-slot row owns dismissing the armed send flow.
        return withOutbox(
          'contribution',
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled
            startIcon={<ForwardToInbox sx={{ fontSize: '1rem' }} />}
            sx={sendButtonSx}
          >
            {t('contributor.inviteStatusPanel.send')}
          </Button>,
        );
      }
      if (armMode === 'contribution') {
        return withOutbox(
          'contribution',
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              color="primary"
              disabled={isPending}
              onClick={() => setArmMode('none')}
              sx={cancelButtonSx}
            >
              {t('common.cancel')}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={inviteThisRowDisabled}
              onClick={() => send([])}
              startIcon={sendStartIcon}
              sx={sendButtonSx}
            >
              {t('contributor.inviteStatusPanel.send')}
            </Button>
          </Stack>,
        );
      }
      return withOutbox(
        'contribution',
        roleStagedForInvite || isFlowSlotAssetsUi ? null : (
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={inviteThisRowDisabled}
            onClick={() => setArmMode('contribution')}
            startIcon={inviteListButtonStartIcon}
            sx={inviteListButtonSx}
          >
            {t('contributor.inviteStatusPanel.inviteList')}
          </Button>
        ),
      );
    }

    // time slot row
    if (armMode === 'timeSlot' && roleSlotId) {
      return withOutbox(
        'timeSlot',
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Button
            size="small"
            variant="outlined"
            color="primary"
            disabled={isPending}
            onClick={() => setArmMode('none')}
            sx={cancelButtonSx}
          >
            {t('common.cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={inviteThisRowDisabled}
            onClick={() => send([roleSlotId])}
            startIcon={sendStartIcon}
            sx={sendButtonSx}
          >
            {t('contributor.inviteStatusPanel.send')}
          </Button>
        </Stack>,
      );
    }
    return withOutbox(
      'timeSlot',
      slotStagedForInvite || isFlowSlotAssetsUi ? null : (
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={inviteThisRowDisabled || !roleSlotId}
          onClick={() => setArmMode('timeSlot')}
          startIcon={inviteListButtonStartIcon}
          sx={inviteListButtonSx}
        >
          {t('contributor.inviteStatusPanel.inviteList')}
        </Button>
      ),
    );
  };

  const rowSx = {
    'flexDirection': 'row' as const,
    'alignItems': 'center',
    'justifyContent': 'space-between',
    'gap': 1,
    'py': 0.5,
    'px': 0.5,
    'borderRadius': 1,
    'bgcolor': 'action.hover',
    '&:hover': { bgcolor: 'action.selected' },
  };

  return (
    <Box sx={{ mb: 1.5 }}>
      {creditsBanner}
      <Stack spacing={0.75}>
        <Stack sx={rowSx}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            <AssignmentIndOutlined sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }} />
            <Typography variant="body2" color="text.secondary" fontWeight={600} noWrap>
              {t('contributor.inviteStatusPanel.contribution')}
            </Typography>
          </Stack>
          {renderStatusOrActions('contribution', roleInvited, roleAccepted)}
        </Stack>

        {timeSlot != null && (
          <>
            <Divider flexItem sx={{ borderStyle: 'dashed' }} />
            <Stack sx={rowSx}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                <EventOutlined sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }} />
                <Typography variant="body2" color="text.secondary" fontWeight={600} noWrap>
                  {t('contributor.inviteStatusPanel.timeSlot')}
                </Typography>
              </Stack>
              {renderStatusOrActions('timeSlot', timeSlot.invited, timeSlot.accepted, timeSlot.roleSlotId)}
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
}
